import { Stack } from '@mui/material';
import { FormikProvider, useFormik } from 'formik';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { Address } from '@woovi/form';
import { ActionButton } from '@woovi/ui';

type PaymentMethodCreditCardBillingAddressArgs = {
  setBillingAddress: () => void;
};

export type BillingAddressType = {
  zipcode: string;
  street: string;
  number: string;
  neighborhood: string;
  city: string;
  state: string;
  complement: string;
};

const PaymentMethodCreditCardAddBillingAddressStep = ({
  setBillingAddress,
}: PaymentMethodCreditCardBillingAddressArgs): ReactNode => {
  const { t } = useTranslation();

  const validationAddressSchema = yup.object().shape({
    zipcode: yup.string().required(t('Zipcode is required')),
    street: yup.string().required(t('Street is required')),
    number: yup.string().required(t('Number is required')),
    neighborhood: yup.string().required(t('Neighborhood is required')),
    city: yup.string().required(t('City is required')),
    state: yup.string().required(t('State is required')),
    complement: yup.string(),
  });

  const formikAddress = useFormik<BillingAddressType>({
    validateOnBlur: true,
    validateOnMount: true,
    enableReinitialize: true,
    initialValues: {
      zipcode: '',
    },
    validationSchema: validationAddressSchema,
    onSubmit: setBillingAddress,
  });

  const { isValid, handleSubmit } = formikAddress;

  return (
    <FormikProvider value={formikAddress}>
      <Stack spacing={2}>
        <Address showHeader={false} />
        <ActionButton
          data-testid={'next-step-billing-address'}
          type='submit'
          disabled={!isValid}
          onClick={handleSubmit}
          sx={(theme) => ({
            color: theme.palette.common.white,
            backgroundColor: theme.palette.primary.main,
            '&:hover': {
              color: theme.palette.common.white,
              backgroundColor: theme.palette.primary.main,
            },
            '&:focus': {
              color: theme.palette.common.white,
              backgroundColor: theme.palette.primary.main,
            },
          })}
        >
          {t('Next step')}
        </ActionButton>
      </Stack>
    </FormikProvider>
  );
};

export default PaymentMethodCreditCardAddBillingAddressStep;
